import API from "@/api";
import { setUser, captureException } from "@sentry/browser";

const moment = require("moment");

const CUSTOMER_TYPES = ["Physical", "Legal"];
const SET_USER = "SET_USER";
const SET_PROFILE_PICTURE = "SET_PROFILE_PICTURE";
const SET_OLD_USER = "SET_OLD_USER";
const SET_CLOSED_CITIES = "SET_CLOSED_CITIES";
const SET_SHOW_EMAIL_NOT_VERIFIED_DIALOG = "SET_SHOW_EMAIL_NOT_VERIFIED_DIALOG";

export default {
  namespaced: true,

  state: {
    old_user: {
      type: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      cpf: "",
      cnpj: ""
    },
    user: null,
    closedCities: [],
    showEmailNotVerifiedDialog: false
  },

  actions: {
    decodeToken({ commit }, token) {
      let base64Url = token.split(".")[1];
      let base64 = base64Url.replace("-", "+").replace("_", "/");
      return JSON.parse(window.atob(base64));
    },

    saveAccountableOnLocalStorage(context, accountable) {
      localStorage.accountable = JSON.stringify({
        id: accountable.id,
        type: accountable.type
      });
    },

    async refreshUser({ dispatch, state }) {
      dispatch("getUser", state.user.id);
    },

    async getUser({ dispatch, commit }, accountID) {
      try {
        const user = await API.User.searchByAccountId(accountID);
        dispatch("saveAccountableOnLocalStorage", user.accountable);
        dispatch("setClosedCities");
        commit("SET_USER", user);
      } catch (e) {
        throw e;
      }
    },

    async authentication({ dispatch }, creds) {
      try {
        let {
          data: {
            data: { token, account }
          }
        } = await API.Authentication.authenticate(creds);

        const isCustomer = CUSTOMER_TYPES.includes(account.accountable_type);

        if (isCustomer) {
          localStorage.authentication = token;
          await dispatch("getUser", account.id);
        } else {
          throw "Você não tem acesso à esta aplicação";
        }
      } catch (e) {
        if (e.response.status === 404)
          return await dispatch("authenticationOldAPI", creds);

        throw "Usuário ou senha incorretos";
      }
    },

    async authenticationOldAPI({ commit }, creds) {
      try {
        let response = await API.Authentication.authenticateOldAPI(creds);
        commit("SET_OLD_USER", response.user);
      } catch (e) {
        throw "Usuário ou senha incorretos";
      }
    },

    async isAuthenticated({ dispatch, state }) {
      try {
        let authentication = localStorage.authentication;

        if (!authentication && !state.user) throw "Não autorizado";

        let token_data = await dispatch("decodeToken", authentication);
        let expires_at = moment(token_data.exp * 1000);

        if (expires_at < moment()) {
          let checkToken = await API.Authentication.checkToken();
          if (!checkToken) throw "Não autorizado";
        }
        if (!state.user) await dispatch("getUser", token_data.user_id);
        return true;
      } catch (e) {
        dispatch("logout");
        return false;
      }
    },

    clearLocalStorage() {
      localStorage.removeItem("authentication");
      localStorage.removeItem("accountable");
    },

    logout({ commit, dispatch }) {
      commit("newOrder/RESET_STATE", null, {
        root: true
      });
      dispatch("clearLocalStorage");
      commit("SET_USER", null);
    },

    async uploadProfilePicture({ commit, state }, profilePicture) {
      try {
        let profile_picture = await API.Cloudinary.upload(profilePicture);
        await API.User.updateProfilePicture(profile_picture);
        commit("SET_PROFILE_PICTURE", profile_picture);
      } catch (e) {
        throw e;
      }
    },

    async updatePersonalData({ dispatch, state }, personalData) {
      try {
        const accountData = {
          first_name: personalData.first_name,
          last_name: personalData.last_name
        };
        const profileData = {
          city_name: personalData.city_name,
          state_name: personalData.state_name
        };
        const phoneData = personalData.phone;

        await dispatch("updateAccount", accountData);
        await dispatch("updateProfile", profileData);
        await dispatch("updatePhone", phoneData);
        await dispatch("getUser", state.user.id);
      } catch (e) {
        throw e;
      }
    },

    async updateAccount({ state }, account) {
      try {
        await API.User.updateAccount(account);
      } catch (e) {
        throw e;
      }
    },

    async updateProfile({ state }, profile) {
      try {
        await API.User.updateProfile(profile);
      } catch (e) {
        throw e;
      }
    },

    async updatePhone({ state }, phone) {
      try {
        await API.User.updatePhone(phone);
      } catch (e) {
        throw e;
      }
    },

    async createPaymentGateways({ rootState, state }) {
      try {
        let user = state.user;
        let type =
          user.accountable.type.charAt(0).toUpperCase() +
          user.accountable.type.slice(1);
        user.address = rootState.newOrder.address;

        if (!user.payment_gateway_account)
          await API[type].createPaymentGatewayAccount(user);

        await API[type].createPaymentGatewayCustomer(user);
        await API[type].createOnGateway(user.id);
      } catch (e) {
        setUser({ email: state.user.email });
        captureException(e);
        throw "Ocorreu um erro ao criar a conta ou cliente no meio de pagamento, contate nosso suporte!";
      }
    },

    async sendEmailVerification({ state, commit }) {
      try {
        await API.User.sendEmailVerification();

        let user = state.user;
        user.status = "waiting_email_verification";
        commit("SET_USER", user);
      } catch (e) {
        throw e;
      }
    },

    async verifyEmail({ commit }, token) {
      try {
        await API.User.verifyEmail(token);
      } catch (e) {
        throw e;
      }
    },

    async sendPasswordRecovery({ state }, email) {
      try {
        await API.User.sendPasswordRecovery(email);
      } catch (e) {
        throw e;
      }
    },

    async sendPasswordReset({ state }, payload) {
      try {
        await API.User.sendPasswordReset(payload);
      } catch (e) {
        throw e;
      }
    },

    async checksForClosedCitiesExistence({ rootState }) {
      try {
        let cities = await API.Addresses.getCities();
        cities = cities.filter(city =>
          rootState.addresses.closedCities.includes(
            city.attributes.display_name
          )
        );
        return cities.map(city => city.attributes.display_name);
      } catch (e) {
        throw e;
      }
    },

    async setClosedCities({ dispatch, commit }, payload) {
      try {
        let closedCities = await dispatch("checksForClosedCitiesExistence");
        commit("SET_CLOSED_CITIES", closedCities);
      } catch (e) {
        throw e;
      }
    },

    showEmailNotVerifiedDialog({ commit }) {
      commit(SET_SHOW_EMAIL_NOT_VERIFIED_DIALOG, true);
    },

    closeEmailNotVerifiedDialog({ commit }) {
      commit(SET_SHOW_EMAIL_NOT_VERIFIED_DIALOG, false);
    },

    async checkEmail({}, email) {
      try {
        await API.Accounts.verifyEmail(email);
      } catch (e) {
        throw e;
      }
    }
  },

  mutations: {
    [SET_CLOSED_CITIES](state, payload) {
      state.closedCities = payload;
    },
    [SET_USER](state, payload) {
      state.user = payload;
    },
    [SET_PROFILE_PICTURE](state, payload) {
      state.user.profile.profile_picture = payload;
    },
    [SET_OLD_USER](state, payload) {
      state.old_user.first_name = payload.first_name ? payload.first_name : "";
      state.old_user.last_name = payload.last_name ? payload.last_name : "";
      state.old_user.email = payload.email ? payload.email : "";
      state.old_user.phone = payload.phone
        ? payload.phone.area_code + payload.phone.number
        : "";

      if (payload.cnpj) {
        state.old_user.type = "legal";
        state.old_user.cnpj = payload.cnpj;
      } else if (payload.cpf) {
        state.old_user.type = "physical";
        state.old_user.cpf = payload.cpf;
      }
    },
    [SET_SHOW_EMAIL_NOT_VERIFIED_DIALOG](state, payload) {
      state.showEmailNotVerifiedDialog = payload;
    }
  },

  getters: {
    hasNoVerifiedEmail(state) {
      return state.user && state.user.status === "waiting_email_verification";
    },
    update_your_data(state) {
      return state.old_user.email != "";
    },

    full_name(state) {
      return state.user
        ? state.user.first_name + " " + state.user.last_name
        : "";
    },

    phone_number(state) {
      return state.user && state.user.phones && state.user.phones.length
        ? `(${state.user.phones[0].area_code}) ${state.user.phones[0].number}`
        : "";
    },

    birthdate(state) {
      return state.user
        ? moment(state.user.profile.birthdate).format("DD/MM/Y")
        : "";
    },

    ddd(state) {
      return state.user && state.user.phones && state.user.phones.length
        ? state.user.phones[0].area_code
        : "";
    },

    cpf(state) {
      return state.user && state.user.accountable.type === "physicals"
        ? state.user.accountable.cpf
        : "";
    },

    cnpj(state) {
      return state.user && state.user.accountable.type === "legals"
        ? state.user.accountable.cpf
        : "";
    },

    answeredTermsOfUse(state) {
      return (
        "accounts_terms_of_uses" in state.user &&
        state.user.accounts_terms_of_uses.length > 0 &&
        state.user.accounts_terms_of_uses[0].accept
      );
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/user.js